import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOAuthCallback } from '@/api/queries/auth';
import { useAuthStore } from '@/store/auth';
import { paths } from '@/routes/paths';
import { Spinner } from '@/components/ui/Spinner';
import { PageContainer } from '@/components/layout/PageContainer';
import { useUIStore } from '@/store/ui';
import { isAPIError } from '@/api/error';

// Facebook OAuth callback — mirrors the Google flow.
// Backend endpoints (parallel task): /auth/oauth/facebook/start and
// /auth/oauth/facebook/callback. If the backend has not been wired up
// yet, the redirect from auth-landing will already have failed before
// reaching this page; defensive handling here covers the edge case
// where Facebook redirects us back with `error` instead of `code`.
export function OAuthFacebookCallbackPage(): JSX.Element {
  const [params] = useSearchParams();
  const code = params.get('code');
  const state = params.get('state') ?? '';
  const oauthError = params.get('error');
  const navigate = useNavigate();
  const cb = useOAuthCallback();
  const setHasToken = useAuthStore((s) => s.setHasToken);
  const pushToast = useUIStore((s) => s.pushToast);
  const { t } = useTranslation();

  useEffect(() => {
    if (oauthError || !code) {
      pushToast({
        body: t('auth.oauth_cancelled', { defaultValue: t('errors.generic') }),
        variant: 'error',
      });
      navigate(paths.authLanding, { replace: true });
      return;
    }
    void (async () => {
      try {
        const r = await cb.mutateAsync({ provider: 'facebook', code, state });
        setHasToken(true);
        navigate(r.user.onboarding_completed ? paths.home : paths.onboardingProfile, {
          replace: true,
        });
      } catch (err) {
        const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
        pushToast({ body: t(key, { defaultValue: t('errors.generic') }), variant: 'error' });
        navigate(paths.authLanding, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContainer>
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Spinner size={32} />
      </div>
    </PageContainer>
  );
}
